import React, { useState, useEffect, useRef } from 'react';
import OrderLedgerBuilder from './OrderLedgerBuilder';
import PositionLedgerSummary from './PositionLedgerSummary';
import PositionLedgerTable from './PositionLedgerTable';
import './PositionLedger.css';

// ─── Dashboard palette (used only for dynamic/data-driven values) ─────────────
const C = {
  bg:     '#08101e',
  bg1:    '#0c1525',
  bg2:    '#101b2e',
  border: '#1e2d42',
  border2:'#243548',
  text:   '#e8edf5',
  body:   '#b8c8dc',
  meta:   '#6a7d95',
  green:  '#00e896',
  cyan:   '#00c8ff',
  purple: '#9966ff',
  font:   "'DM Mono', ui-monospace, monospace",
};

// Per-tab config — accent is the only dynamic value needed inline
const TABS = [
  { key: 'stocks',  label: 'Stocks',  accent: C.green,  countColor: C.green  },
  { key: 'options', label: 'Options', accent: C.purple, countColor: C.purple },
];

// ─── CSV helpers ──────────────────────────────────────────────────────────────

/** Escape a single cell value for CSV. */
function csvCell(val) {
  if (val === null || val === undefined) return '';
  const str = String(val);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/** Convert an array of plain objects to a CSV string. */
function toCsvString(rows) {
  if (!rows || rows.length === 0) return '';
  const headers = Object.keys(rows[0]);
  const lines = [
    headers.map(csvCell).join(','),
    ...rows.map(row => headers.map(h => csvCell(row[h])).join(',')),
  ];
  return lines.join('\n');
}

/** Trigger a browser download of a CSV string. */
function downloadCsv(csvString, filename) {
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Map a ledger position row to a flat export object.
 * Column order mirrors PositionLedgerTable headers exactly.
 */
function positionToExportRow(position, isOption) {
  const row = {
    Status:         position.status             || '',
    Symbol:         position.symbol             || '',
  };

  if (isOption) {
    row['Option Type'] = position.option_type?.toUpperCase() || '';
    row['Strike']      = position.strike                     || '';
    row['Expiry']      = position.expiration                 || '';
  }

  row['Direction']      = position.direction?.toUpperCase()  || '';
  row['Qty']            = position.quantity                  ?? '';
  row['Entry $']        = position.avg_entry_price           ?? '';
  row['Entry Fill']     = position.entry_filled_at           || '';
  row['Entry Type']     = position.entry_order_type          || '';
  row['Entry Limit']    = position.entry_limit_price         ?? '';
  row['Entry Slippage'] = position.entry_slippage            ?? '';
  row['Exit $']         = position.exit_fill_price           ?? '';
  row['Exit Fill']      = position.exit_filled_at            || '';
  row['Exit Type']      = position.exit_order_type           || '';
  row['Exit Limit']     = position.exit_limit_price          ?? '';
  row['Exit Slippage']  = position.exit_slippage             ?? '';
  row['P&L ($)']        = position.total_pnl_num             ?? '';
  row['R/R']            = position.rr                        ?? '';
  row['TP']             = position.tp_limit                  ?? '';
  row['SL Stop']        = position.sl_stop                   ?? '';
  row['SL Limit']       = position.sl_limit                  ?? '';
  row['Hold']           = position.hold_time                 || '';
  row['Submit']         = position.entry_submitted_at        || '';

  return row;
}

/**
 * Build and download CSV for a given set of positions.
 * @param {Array}  positions  - ledgerView.stocks or ledgerView.options
 * @param {string} assetClass - 'stock' | 'option'
 * @param {string} label      - date string or context label for filename, e.g. "2026-03-24"
 */
function exportPositionsCsv(positions, assetClass, label) {
  if (!positions || positions.length === 0) return;
  const isOption = assetClass === 'option';
  const rows     = positions.map(p => positionToExportRow(p, isOption));
  const csv      = toCsvString(rows);
  const suffix   = label ? `_${label}` : '';
  downloadCsv(csv, `position_ledger_${assetClass}s${suffix}.csv`);
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * PositionLedger
 *
 * Props (unchanged except exportLabel added):
 *   rawOrders     {Array}    - raw Alpaca orders (used when ledgerView not supplied)
 *   ledgerView    {Object}   - pre-built { stocks, options, summary } from parent (optional)
 *   proceedEvals  {Array}    - PROCEED eval bars for option strategy chips
 *   onOptionClick {Function} - called with (position, matchedEval) on option row click
 *   exportLabel   {string}   - appended to CSV filename, e.g. "2026-03-24" (optional)
 */
function PositionLedger({ rawOrders, ledgerView: ledgerViewProp, proceedEvals = [], onOptionClick, exportLabel }) {
  const [ledgerViewInternal, setLedgerViewInternal] = useState({ stocks: [], options: [], summary: {} });
  const [isLoading,  setIsLoading]  = useState(true);
  const [activeTab,  setActiveTab]  = useState('stocks');
  const ledgerBuilderRef = useRef(new OrderLedgerBuilder());

  // If pre-computed ledgerView is passed in from TradingCommand, use it directly.
  // Otherwise fall back to building internally from rawOrders.
  const ledgerView = ledgerViewProp || ledgerViewInternal;

  useEffect(() => {
    if (ledgerViewProp) { setIsLoading(false); return; }
    if (!rawOrders || rawOrders.length === 0) { setIsLoading(false); return; }
    try {
      const view = ledgerBuilderRef.current.processOrderHistory(rawOrders);
      setLedgerViewInternal(view);
    } catch (err) {
      console.error('❌ PositionLedger build error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [rawOrders, ledgerViewProp]);

  if (isLoading) {
    return (
      <div className="pl-empty" style={{ textAlign: 'center', padding: '32px' }}>
        <div style={{ fontFamily: C.font, fontSize: 10, color: C.meta }}>
          Loading positions…
        </div>
      </div>
    );
  }

  const activeStocks  = ledgerView.summary.active_stocks  || 0;
  const closedStocks  = ledgerView.summary.closed_stocks  || 0;
  const activeOptions = ledgerView.summary.active_options || 0;
  const closedOptions = ledgerView.summary.closed_options || 0;

  const tabCounts = {
    stocks:  { active: activeStocks,  closed: closedStocks  },
    options: { active: activeOptions, closed: closedOptions },
  };

  // Resolve data for active tab — used by both the table and the export button
  const activeTabData    = activeTab === 'stocks' ? ledgerView.stocks  : ledgerView.options;
  const activeTabAsset   = activeTab === 'stocks' ? 'stock'            : 'option';
  const activeTabHasData = activeTabData && activeTabData.length > 0;

  const handleExport = () => exportPositionsCsv(activeTabData, activeTabAsset, exportLabel);

  return (
    <div className="position-ledger">

      {/* Summary strip */}
      <PositionLedgerSummary summary={ledgerView.summary} />

      {/* Card wrapper — static styles from CSS */}
      <div className="ledger-card">

        {/* Tab bar + export button — flex row */}
        <div
          className="ledger-tab-bar"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          {/* ── Tabs (left) ── */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {TABS.map(tab => {
              const isActive = activeTab === tab.key;
              const counts   = tabCounts[tab.key];
              return (
                <button
                  key={tab.key}
                  className="ledger-tab"
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    color:        isActive ? C.text  : C.meta,
                    borderBottom: isActive ? `2px solid ${tab.accent}` : '2px solid transparent',
                    background:   isActive ? C.bg2   : 'transparent',
                  }}
                >
                  {tab.label}
                  {counts.active > 0 && (
                    <span className="ledger-tab-count" style={{
                      color:       tab.accent,
                      background:  `${tab.accent}18`,
                      borderColor: `${tab.accent}50`,
                    }}>
                      {counts.active}
                    </span>
                  )}
                  {counts.closed > 0 && (
                    <span className="ledger-tab-count" style={{
                      color:       C.meta,
                      background:  `${C.meta}12`,
                      borderColor: `${C.meta}40`,
                    }}>
                      {counts.closed}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* ── Export button (right) — hidden when tab is empty ── */}
          {activeTabHasData && (
            <button
              onClick={handleExport}
              title={`Download ${activeTab} as CSV`}
              style={{
                display:       'flex',
                alignItems:    'center',
                gap:           '5px',
                padding:       '4px 10px',
                marginRight:   '8px',
                borderRadius:  '4px',
                border:        `1px solid ${C.border2}`,
                background:    'transparent',
                color:         C.meta,
                fontFamily:    C.font,
                fontSize:      '10px',
                cursor:        'pointer',
                letterSpacing: '0.4px',
                transition:    'color 0.15s, border-color 0.15s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.color       = C.text;
                e.currentTarget.style.borderColor = C.cyan;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.color       = C.meta;
                e.currentTarget.style.borderColor = C.border2;
              }}
            >
              {/* Download icon — pure SVG, no emoji */}
              <svg
                width="11" height="11" viewBox="0 0 12 12"
                fill="none" xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6 1v7M3 6l3 3 3-3M2 11h8"
                  stroke="currentColor" strokeWidth="1.5"
                  strokeLinecap="round" strokeLinejoin="round"
                />
              </svg>
              CSV
            </button>
          )}
        </div>

        {/* Scrollable content */}
        <div className="ledger-scroll-area">
          {activeTab === 'stocks' && (
            <PositionLedgerTable
              positions={ledgerView.stocks}
              assetClass="stock"
            />
          )}
          {activeTab === 'options' && (
            <PositionLedgerTable
              positions={ledgerView.options}
              assetClass="option"
              proceedEvals={proceedEvals}
              onOptionClick={onOptionClick}
            />
          )}
        </div>

      </div>
    </div>
  );
}

export default PositionLedger;